import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Block } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(name, password);
      } else {
        await register(name, password);
      }
      navigate('/');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      // Check if it's a disabled account error
      if (err.response?.status === 403 || errorMessage.includes('disabled')) {
        setIsDisabled(true);
        setError('');
      } else {
        setIsDisabled(false);
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show disabled message if account is disabled
  if (isDisabled) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          p: 3
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            textAlign: 'center',
            maxWidth: 600,
            width: '100%'
          }}
        >
          <Box
            sx={{
              fontSize: 80,
              color: 'error.main',
              mb: 3,
              lineHeight: 1,
            }}
          >
            😢
          </Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              mb: 2,
              color: 'text.primary'
            }}
          >
            Account Disabled
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: 'text.secondary',
              mb: 4
            }}
          >
            Your account has been temporarily disabled
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h5" align="center" gutterBottom>
          {isLogin ? 'Login' : 'Sign Up'}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
          />
          <Button
            fullWidth
            variant="contained"
            type="submit"
            sx={{ mt: 2 }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress color="inherit" size={20}/>
            ) : (
              isLogin ? 'Login' : 'Sign Up'
            )}
          </Button>
        </form>

        <Button
          fullWidth
          onClick={() => setIsLogin(!isLogin)}
          sx={{ mt: 2 }}
        >
          {isLogin ? 'Need an account? Sign Up' : 'Already have an account? Login'}
        </Button>

        <Box sx={{ 
          mt: 2, 
          textAlign: 'center',
          fontSize: '14px',
          color: 'text.secondary'
        }}>
          By using this service, you agree to our{' '}
          <Link 
            to="/terms-and-privacy" 
            style={{ 
              color: 'inherit',
              textDecoration: 'underline'
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service & Privacy Policy
          </Link>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login; 